import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {{
 *   mobileButton: HTMLButtonElement,
 *   desktopButton: HTMLButtonElement,
 *   mobilePrice: HTMLElement,
 *   desktopPrice: HTMLElement,
 *   mobileComparePrice: HTMLElement,
 *   desktopComparePrice: HTMLElement,
 *   desktopInfo: HTMLElement,
 * }} StickyAtcRefs
 *
 * @typedef {{
 *   title: string,
 *   optionLabels: string[],
 *   planLabel: string,
 *   price: string,
 *   compare: string,
 * }} MirrorState
 */

/**
 * Sticky Add-to-Cart bar.
 *
 * - Activates only on templates listed in `data-active-on-templates` (default: "product").
 * - Observes the real ATC button(s) with IntersectionObserver; visible only when none are in view
 *   AND none are disabled (sold out).
 * - Mirrors the buy box's title, variant options, selling-plan label, price, and compare-at price
 *   by reading the buy-box DOM, refreshed on `variant:selected` and `skio::update-selling-plan`
 *   plus a MutationObserver on Skio + the theme's price container.
 * - Click delegates to the real ATC button via `.click()` so all add-to-cart logic
 *   (form submit, sections refresh, cart drawer open, error handling, Skio analytics) lives in one place.
 *
 * @extends {Component<StickyAtcRefs>}
 */
class StickyAtc extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<Element>} */
  #realAtcButtons = new Set();

  /** @type {Set<Element>} */
  #intersectingAtcs = new Set();

  /** @type {(() => void) | null} */
  #cleanupListeners = null;

  /** @type {MutationObserver | null} */
  #priceObserver = null;

  /** @type {MutationObserver | null} */
  #bodyObserver = null;

  /** @type {boolean} */
  #enabled = false;

  /**
   * Last successfully-read state, used to avoid blanking during re-render races.
   * @type {{ optionLabels: string[], planLabel: string, price: string, compare: string, title: string }}
   */
  #lastState = { optionLabels: [], planLabel: '', price: '', compare: '', title: '' };

  connectedCallback() {
    super.connectedCallback();

    // Template gate. Active only on listed templates.
    const activeTemplates = (this.dataset.activeOnTemplates || 'product')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const currentTemplate = (this.dataset.template || '').split('.')[0]; // strip suffix
    if (!activeTemplates.includes(currentTemplate)) return;

    // A/B test gate. Intelligems is configured to set data-experiment-sticky-atc="on"
    // on <body> for the variant group. Control gets nothing (bar stays inert).
    // URL override ?sticky_atc=on|off forces the state for QA/design preview.
    const urlFlag = new URL(window.location.href).searchParams.get('sticky_atc');
    if (urlFlag === 'off') return;
    if (urlFlag === 'on') {
      this.#enable();
      return;
    }

    // The body attribute may land before our connectedCallback (returning
    // visitor, cached cohort) OR after (first-visit cohort fetch, view-transition
    // page swap). A MutationObserver covers both timings — we activate the moment
    // the attr flips to "on", regardless of who wins the race.
    if (document.body.dataset.experimentStickyAtc === 'on') {
      this.#enable();
    }
    this.#bodyObserver = new MutationObserver(() => {
      const isOn = document.body.dataset.experimentStickyAtc === 'on';
      if (isOn && !this.#enabled) this.#enable();
      else if (!isOn && this.#enabled) this.#disable();
    });
    this.#bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-experiment-sticky-atc'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#bodyObserver?.disconnect();
    this.#bodyObserver = null;
    this.#disable();
  }

  /**
   * Wires up everything: finds ATC button(s), observes them for visibility,
   * mirrors buy-box state, and listens for variant/plan changes. Idempotent —
   * safe to call multiple times; second call is a no-op. If no ATC button
   * exists on the page (snippet rendered on a non-PDP surface), returns
   * without marking enabled, so a later attribute flip can try again.
   */
  #enable() {
    if (this.#enabled) return;

    const atcButtons = document.querySelectorAll(
      'add-to-cart-component button[ref="addToCartButton"]'
    );
    if (atcButtons.length === 0) return;
    this.#enabled = true;
    atcButtons.forEach((btn) => this.#realAtcButtons.add(btn));

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#intersectingAtcs.add(entry.target);
          else this.#intersectingAtcs.delete(entry.target);
        }
        this.#updateVisibility();
      },
      { rootMargin: '0px', threshold: 0 }
    );
    for (const btn of this.#realAtcButtons) this.#observer.observe(btn);

    this.#mirrorState();

    const onVariantSelected = () => this.#mirrorState();
    const onPlanUpdate = () => this.#mirrorState();
    document.addEventListener(ThemeEvents.variantSelected, onVariantSelected);
    document.addEventListener('skio::update-selling-plan', onPlanUpdate);

    // Observe wherever the price actually updates. For Skio products that's the shadow root
    // of <skio-plan-picker> (the .skio-price inside the selected group). For non-Skio products
    // it's the theme's [ref="priceContainer"]. Observe whichever exists.
    this.#priceObserver = new MutationObserver(() => this.#mirrorState());
    const skioEl = document.querySelector('skio-plan-picker');
    const skioObserveTarget = skioEl?.shadowRoot || skioEl;
    if (skioObserveTarget) {
      this.#priceObserver.observe(skioObserveTarget, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    const priceContainer = document.querySelector('#product-main [ref="priceContainer"]');
    if (priceContainer) {
      this.#priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    this.#cleanupListeners = () => {
      document.removeEventListener(ThemeEvents.variantSelected, onVariantSelected);
      document.removeEventListener('skio::update-selling-plan', onPlanUpdate);
    };
  }

  /**
   * Tears down everything #enable set up. Called when the body attribute
   * flips away from "on" mid-session, OR from disconnectedCallback. Resets
   * visible state — otherwise the bar could stay on-screen after being
   * toggled off via the experiment.
   */
  #disable() {
    if (!this.#enabled) return;
    this.#enabled = false;

    this.#observer?.disconnect();
    this.#observer = null;
    this.#priceObserver?.disconnect();
    this.#priceObserver = null;
    this.#cleanupListeners?.();
    this.#cleanupListeners = null;
    this.#realAtcButtons.clear();
    this.#intersectingAtcs.clear();

    this.dataset.state = 'hidden';
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sticky-atc-visible');
  }

  /**
   * Click handler bound declaratively via `on:click="/handleClick"` on both mobile and desktop buttons.
   * Delegates to whichever real ATC button is currently usable (enabled, in document order).
   */
  handleClick(event) {
    event?.preventDefault?.();
    const target = this.#pickDelegateButton();
    if (!target) return;
    target.click();
  }

  /**
   * Picks a real ATC button to delegate to. Prefers a non-disabled, visible one;
   * falls back to the first non-disabled.
   */
  #pickDelegateButton() {
    let firstEnabled = null;
    for (const btn of this.#realAtcButtons) {
      if (btn.disabled) continue;
      if (!firstEnabled) firstEnabled = btn;
      const rect = btn.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return btn;
    }
    return firstEnabled;
  }

  #updateVisibility() {
    const anyEnabled = [...this.#realAtcButtons].some((b) => !b.disabled);
    const anyInView = this.#intersectingAtcs.size > 0;
    const shouldShow = anyEnabled && !anyInView;
    this.dataset.state = shouldShow ? 'visible' : 'hidden';
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    // Manage focusability of inner buttons.
    for (const btn of [this.refs.mobileButton, this.refs.desktopButton]) {
      if (!btn) continue;
      btn.tabIndex = shouldShow ? 0 : -1;
    }
    // Toggle a body class so floating widgets (gorgias, userway) can bump up.
    document.body.classList.toggle('sticky-atc-visible', shouldShow);
  }

  /**
   * Reads the buy-box DOM and updates the sticky bar's visible content.
   */
  #mirrorState() {
    const productMain = document.getElementById('product-main');
    if (!productMain) return;
    const state = this.#resolveState(this.#readBuyBoxState(productMain));
    this.#renderPrices(state);
    this.#renderDesktopInfo(state);
    this.#renderMobileAria(state);
  }

  /**
   * Reads raw state from the buy-box DOM. Any field may be empty if the
   * corresponding element is mid-render or absent.
   * @param {HTMLElement} productMain
   * @returns {{ title: string, optionLabels: string[], planLabel: string, price: string, compare: string }}
   */
  #readBuyBoxState(productMain) {
    // Title: prefer h1 inside the buy box, fall back to document title.
    const title =
      productMain.querySelector('h1')?.textContent?.trim() ||
      document.title.split('|')[0]?.trim() ||
      '';

    // Selected variant option labels (e.g. "16oz Bottles", "3 Pack"). Use the browser's native
    // :checked rather than the data-current-checked attribute (which lags during re-renders).
    // For dropdown-style pickers, also read each <select>'s currently-selected <option>.
    /** @type {string[]} */
    const optionLabels = [];
    productMain.querySelectorAll('.variant-option input[type="radio"]:checked').forEach((input) => {
      const label = input.closest('.variant-option__button-label');
      const text = label?.querySelector('.variant-option__button-label__text')?.textContent?.trim();
      if (text) optionLabels.push(text);
    });
    productMain.querySelectorAll('.variant-option__select').forEach((select) => {
      const sel = /** @type {HTMLSelectElement} */ (select);
      const text = sel.options?.[sel.selectedIndex]?.textContent?.trim();
      if (text) optionLabels.push(text);
    });

    // Skio renders inside its own DOM scope (light or shadow depending on browser/version).
    const skioEl = document.querySelector('skio-plan-picker');
    const skioScope = skioEl?.shadowRoot || skioEl;
    let planLabel = '';
    let price = '';
    let compare = '';

    if (skioScope) {
      const titleEl = skioScope.querySelector('.group-container--selected .group-title');
      if (titleEl) {
        const clone = /** @type {Element} */ (titleEl.cloneNode(true));
        clone.querySelectorAll('.savings').forEach((s) => s.remove());
        planLabel = (clone.textContent || '').replace(/\s+/g, ' ').trim();
      }
      const skioPriceEl = skioScope.querySelector('.group-container--selected .skio-price');
      if (skioPriceEl) {
        compare = skioPriceEl.querySelector('s')?.textContent?.trim() || '';
        const clone = /** @type {Element} */ (skioPriceEl.cloneNode(true));
        clone.querySelectorAll('s').forEach((el) => el.remove());
        price = (clone.textContent || '').replace(/\s+/g, ' ').trim();
      }
    }

    // Fallback to theme's price snippet (products without Skio).
    if (!price) {
      const priceContainer = productMain.querySelector('[ref="priceContainer"]');
      price = priceContainer?.querySelector('.price')?.textContent?.trim() || '';
      compare = priceContainer?.querySelector('.compare-at-price')?.textContent?.trim() || compare;
    }

    return { title, optionLabels, planLabel, price, compare };
  }

  /**
   * Applies last-good-state fallback to fields that came back empty, then caches the
   * resolved state for the next read. Price + compare resolve together — if we couldn't
   * read a fresh price the whole price read is suspect, so we keep the last-known compare too.
   * @param {{ title: string, optionLabels: string[], planLabel: string, price: string, compare: string }} raw
   */
  #resolveState(raw) {
    const priceMissing = !raw.price;
    const resolved = {
      title: raw.title || this.#lastState.title,
      optionLabels: raw.optionLabels.length ? raw.optionLabels : this.#lastState.optionLabels,
      planLabel: raw.planLabel || this.#lastState.planLabel,
      price: priceMissing ? this.#lastState.price : raw.price,
      compare: priceMissing ? this.#lastState.compare : raw.compare,
    };
    this.#lastState = resolved;
    return resolved;
  }

  /** @param {MirrorState} state */
  #renderPrices(state) {
    const hasCompare = !!state.compare && state.compare !== state.price;
    if (this.refs.mobilePrice) this.refs.mobilePrice.textContent = state.price || '';
    if (this.refs.desktopPrice) this.refs.desktopPrice.textContent = state.price || '';
    for (const ref of [this.refs.mobileComparePrice, this.refs.desktopComparePrice]) {
      if (!ref) continue;
      ref.textContent = hasCompare ? state.compare : '';
      ref.toggleAttribute('hidden', !hasCompare);
    }
  }

  /** @param {MirrorState} state */
  #renderDesktopInfo(state) {
    const desktopInfo = /** @type {HTMLElement | undefined} */ (this.refs.desktopInfo);
    if (!desktopInfo) return;
    const chips = [...state.optionLabels, state.planLabel].filter(Boolean);
    const parts = [];
    if (state.title) {
      parts.push(`<span class="sticky-atc__title">${this.#escape(state.title)}</span>`);
    }
    for (const chip of chips) {
      if (parts.length > 0) parts.push('<span class="sticky-atc__divider" aria-hidden="true"></span>');
      parts.push(`<span class="sticky-atc__chip">${this.#escape(chip)}</span>`);
    }
    desktopInfo.innerHTML = parts.join('');
  }

  /** @param {MirrorState} state */
  #renderMobileAria(state) {
    const mobileBtn = /** @type {HTMLButtonElement | undefined} */ (this.refs.mobileButton);
    if (!mobileBtn) return;
    const hasCompare = !!state.compare && state.compare !== state.price;
    const optionsLine = [...state.optionLabels, state.planLabel].filter(Boolean).join(', ');
    const summary = optionsLine
      ? `Add ${state.title}, ${optionsLine}, to cart for ${state.price || ''}${
          hasCompare ? `, regularly ${state.compare}` : ''
        }`
      : `Add ${state.title} to cart${state.price ? ` for ${state.price}` : ''}`;
    mobileBtn.setAttribute('aria-label', summary.replace(/\s+/g, ' ').trim());
  }

  /** @param {string} s */
  #escape(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c)
    );
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
